import { IZendeskTicket, IZendeskTicketRequester } from "@kuika/kuika-cl-model/src/models/zendesk";
import { ZendeskTicketStatus } from "./zendesk-status-badge";

export interface IZendeskRequesterOption {
  id: number;
  name: string;
}

export interface IZendeskTicketGroups {
  open: IZendeskTicket[];
  pending: IZendeskTicket[];
  solved: IZendeskTicket[];
}

/** Converts the backend requester list into an id → display-name lookup map. */
export function buildRequesterNameMap(requesters: IZendeskTicketRequester[]): Record<number, string> {
  const map: Record<number, string> = {};

  if (!requesters) return map;

  for (const requester of requesters) {
    if (requester && typeof requester.id === "number" && requester.name) {
      map[requester.id] = requester.name;
    }
  }

  return map;
}

/** Resolves the display name of the user who opened a ticket, when it is known. */
export function getRequesterName(ticket: IZendeskTicket, requesterNames: Record<number, string>): string | undefined {
  return requesterNames[ticket.requesterId];
}

/**
 * Distinct creators present in the given tickets, each with a display name, sorted alphabetically.
 * Used to populate the "açan kişi" (creator) filter dropdown. A creator whose name could not be
 * resolved still appears (labelled by id) so their tickets remain filterable.
 */
export function getRequesterOptions(
  tickets: IZendeskTicket[],
  requesterNames: Record<number, string>
): IZendeskRequesterOption[] {
  const optionsById: Record<number, IZendeskRequesterOption> = {};

  for (const ticket of tickets) {
    const id: number = ticket.requesterId;
    if (id === undefined || id === null || optionsById[id]) continue;
    optionsById[id] = { id, name: requesterNames[id] ?? `#${id}` };
  }

  return Object.values(optionsById).sort((a: IZendeskRequesterOption, b: IZendeskRequesterOption) =>
    a.name.localeCompare(b.name, "en", { sensitivity: "base" })
  );
}

/** Whether a ticket was opened by the selected creator. A null selection matches every ticket. */
export function ticketMatchesCreator(ticket: IZendeskTicket, selectedRequesterId: number | null): boolean {
  if (selectedRequesterId === null) return true;
  return ticket.requesterId === selectedRequesterId;
}

/** Whether a ticket's subject matches the search text. Empty/undefined search matches every ticket. */
export function ticketMatchesSearch(ticket: IZendeskTicket, searchText?: string): boolean {
  if (!searchText) return true;
  return ticket.subject.toLowerCase().includes(searchText.toLowerCase());
}

/**
 * The three display groups the ticket list and the status board both work in. Zendesk has more
 * statuses than the UI shows, so the mapping is deliberate rather than a pass-through: "new"
 * joins Open (it is an open ticket nobody has picked up), and "closed" joins Solved.
 */
export function getTicketDisplayStatus(ticket: IZendeskTicket): ZendeskTicketStatus | undefined {
  if (ticket.status === "new" || ticket.status === "open") return "open";
  if (ticket.status === "pending") return "pending";
  if (ticket.status === "closed" || ticket.status === "solved") return "solved";
  return undefined;
}

/**
 * Splits tickets into the three display groups in one pass, keeping the incoming order.
 * `passesFilters` is applied first so a filtered-out ticket is not counted anywhere.
 */
export function groupTicketsByStatus(
  tickets: IZendeskTicket[],
  passesFilters: (ticket: IZendeskTicket) => boolean
): IZendeskTicketGroups {
  const groups: IZendeskTicketGroups = { open: [], pending: [], solved: [] };

  for (const ticket of tickets) {
    if (!passesFilters(ticket)) continue;

    const status: ZendeskTicketStatus | undefined = getTicketDisplayStatus(ticket);
    if (status) groups[status].push(ticket);
  }

  return groups;
}
